<script lang="ts">import { onMount } from 'svelte';
const { onSendMessage, onStop = undefined, disabled = false, placeholder = '', } = $props();
let inputValue = $state('');
let textareaElement = $state();
onMount(() => {
    if (textareaElement) {
        if (!disabled) {
            setTimeout(() => {
                textareaElement.focus();
            }, 200);
        }
    }
});
$effect(() => {
    if (!disabled && textareaElement) {
        setTimeout(() => {
            textareaElement.focus();
        }, 100);
    }
});
function handleSubmit() {
    if (inputValue.trim() && !disabled) {
        onSendMessage(inputValue.trim());
        inputValue = '';
        if (textareaElement) {
            textareaElement.style.height = 'auto';
        }
    }
}
function handleStop() {
    if (onStop) {
        onStop();
    }
}
function handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
    }
}
function handleInput() {
    if (textareaElement) {
        textareaElement.style.height = 'auto';
        // Set height to scrollHeight, but cap at 500px
        const newHeight = Math.min(textareaElement.scrollHeight, 500);
        textareaElement.style.height = newHeight + 'px';
    }
}
function handlePaste() {
    // Allow paste but trim whitespace
    setTimeout(() => {
        if (textareaElement) {
            inputValue = textareaElement.value;
            handleInput();
        }
    }, 0);
}
function handleFocus() {
    // Ensure input is visible when focused for mobile virtual keyboards
    if (textareaElement) {
        setTimeout(() => {
            textareaElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }, 100);
    }
}
</script>

<div class="chat-input-container">
  <div class="input-wrapper">
    <textarea
      bind:this={textareaElement}
      bind:value={inputValue}
      {placeholder}
      {disabled}
      onkeydown={handleKeydown}
      oninput={handleInput}
      onpaste={handlePaste}
      onfocus={handleFocus}
      class="chat-textarea"
      rows="2"
      aria-label="Chat message input"
    ></textarea>

    {#if disabled}
      <button class="stop-button" onclick={handleStop} aria-label="Stop generation">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="20" height="20" rx="2" fill="currentColor" />
        </svg>
      </button>
    {:else}
      <button
        class="send-button"
        class:has-text={inputValue.trim().length > 0}
        disabled={!inputValue.trim()}
        onclick={handleSubmit}
        aria-label="Send message"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 2L12 22M12 2L2 12M12 2L22 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    {/if}
  </div>
</div>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.chat-input-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.input-wrapper {
  display: flex;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  padding: 20px;
}

.chat-textarea {
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #333333;
  outline: none;
  overflow-y: auto;
  min-height: 20px;
  max-height: 500px;
  margin: 0;
  padding: 0;
  min-width: 0;
  max-width: 100%;
  box-shadow: unset;
  background-color: unset;
  border-radius: unset;
  transition: unset;
}
.chat-textarea::placeholder {
  color: #999999;
}
.chat-textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.chat-textarea::-webkit-scrollbar {
  width: 4px;
}
.chat-textarea::-webkit-scrollbar-track {
  background: transparent;
}
.chat-textarea::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}
.chat-textarea::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
@media (max-width: 739px) {
  .chat-textarea {
    font-size: 16px;
  }
}

.send-button,
.stop-button {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: end;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.send-button:focus-visible:not(:active),
.stop-button:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.send-button:focus,
.stop-button:focus {
  background: transparent;
  outline: none;
}
.send-button:hover,
.stop-button:hover {
  background: transparent;
}
.send-button:focus,
.stop-button:focus {
  outline: 2px solid rgba(0, 106, 255, 0.5);
  outline-offset: 2px;
}
.send-button:active,
.stop-button:active {
  transform: scale(0.95);
}
.send-button svg,
.stop-button svg {
  transition: transform 0.2s ease;
}

.send-button {
  background: #f0f0f0;
  color: #666666;
  /* Active state when input has text */
}
.send-button:hover:not(:disabled) {
  background: #e0e0e0;
  color: #333333;
}
.send-button:disabled {
  background: #f5f5f5;
  color: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.send-button.has-text:not(:disabled) {
  background: #333333;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.send-button.has-text:not(:disabled):hover {
  background: #000000;
  color: #ffffff;
}
.send-button:hover:not(:disabled) svg {
  transform: translateX(1px);
}

.stop-button {
  background: #f0f0f0;
  color: #333333;
}
.stop-button:hover {
  background: #e0e0e0;
  color: #000000;
}
.stop-button svg {
  transform: none;
}
.stop-button:hover svg {
  transform: none;
}

.input-wrapper:focus-within .send-button:focus {
  outline: 2px solid rgba(0, 106, 255, 0.5);
  outline-offset: 2px;
}</style>
